// SETTINGS V2 — Route layout (SETTINGS_V2_SP0 §2).
//
// Owns the section-scoped root class (.settings-v2-root + .p-claude|.p-machineid
// + .mod-nastavenia + .sv2-dotgrid), the hoisted Radix TooltipProvider, and the
// single three-way responsive branch point (SP1 §2):
//   • desktop ≥1280 — page head + rail + scrolling content column (SP0).
//   • tablet 768–1279 — compact page head + SettingsV2TabBar + fluid content.
//   • phone <768 — eyebrow + SettingsV2SectionSelector + fluid content, with
//     SettingsV2PhoneNavSheet mounted (renders null unless phoneNavOpen).
//
// Mirror source: MidasV3Layout.tsx lines 340-354 (root div + provider + side-
// effect sheet import), with flexDirection:"column" because we have a page head
// above the body, and its own scoped sheet.
//
// Scroll model (deliberate, v3-style app scroll): the ROOT is overflow:hidden in
// every mode; exactly ONE content element scrolls per mode (overflowY:auto). On
// desktop the rail is pinned via the row's alignItems:"flex-start".
//
// Portal rule (updated SP1, supersedes the SP0 comment): portals ARE permitted
// to document.body when the portalled node's wrapper carries
// `settings-v2-root p-${theme} mod-nastavenia` + inline background:"transparent"
// (the ConfirmDialogV3 / NewChatDialogV3 pattern — the transparent override
// defeats .settings-v2-root's opaque var(--bg) paint so the scoped CSS vars
// resolve inside the portalled node). Non-portalled children remain the default
// for SP2+ dialogs; nav/SettingsV2PhoneNavSheet.tsx is the reference impl.
//
// Styling stack: inline styles + the scoped CSS-var token sheet + Radix. No
// MUI, no emotion, no styled-props, no shared design tokens.
import React, { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import * as RadixTooltip from "@radix-ui/react-tooltip";

import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { useBreakpoint } from "../../../shared/hooks/useBreakpoint";
import useTranslation from "../../../shared/hooks/useTranslation";
import { getAccount } from "../customerAccountSlice";
import { setActiveSection, setPhoneNavOpen } from "./settingsV2UiSlice";
import { SETTINGS_BASE } from "./settingsV2Paths";
import useSettingsV2Nav from "./useSettingsV2Nav";
import SettingsV2Rail from "./nav/SettingsV2Rail";
import SettingsV2TabBar from "./nav/SettingsV2TabBar";
import SettingsV2SectionSelector from "./nav/SettingsV2SectionSelector";
import SettingsV2PhoneNavSheet from "./nav/SettingsV2PhoneNavSheet";

// Side-effect import — registers the v2 token sheet (scoped to
// .settings-v2-root). Loaded ONCE here so it covers the whole sub-tree; kept
// OUT of src/index.scss (§4 / acceptance §11.10).
import "./settings-v2-tokens.scss";

// In-file page head (§2). Eyebrow = active group label · user display name;
// h1 = "Settings". Motion-gated by the sheet (sv2-header-reveal). The `compact`
// variant (tablet, SP1 §4.3) only tightens the wrapper padding + h1 size — the
// eyebrow content and the reveal animation are identical in both variants.
const SettingsV2PageHead: React.FC<{
  eyebrow: string;
  title: string;
  compact?: boolean;
}> = ({ eyebrow, title, compact = false }) => (
  <div
    style={{
      padding: compact ? "16px 20px 12px" : "18px 28px 0",
      animation: "sv2-header-reveal 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
    }}
  >
    <div className="eyebrow" style={{ color: "var(--mod-accent)", marginBottom: 4 }}>
      {eyebrow}
    </div>
    <h1
      style={{
        margin: 0,
        fontSize: compact ? 23 : 26,
        fontWeight: 700,
        letterSpacing: "-0.01em",
        color: "var(--ink)",
      }}
    >
      {title}
    </h1>
  </div>
);

const SettingsV2Layout: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { translate } = useTranslation("settings");
  const theme = useAppSelector((s) => s.settingsV2Ui.theme);
  const userInfo = useAppSelector((s) => s.customerAccount.userInfo);
  const { groups } = useSettingsV2Nav();

  // Responsive mode (SP1 §4.1). desktop = neither isPhone nor isTablet.
  const { isPhone, isTablet } = useBreakpoint();
  const phoneNavOpen = useAppSelector((s) => s.settingsV2Ui.phoneNavOpen);

  // Data fetch — layout mount effect only (SEC-REQ-2: no import-time network).
  // dispatch is stable, so this fires once on mount.
  useEffect(() => {
    dispatch(getAccount());
  }, [dispatch]);

  // The sheet must not linger across a resize out of phone — the phoneNavOpen
  // seam would otherwise stay true and reopen unexpectedly on the next shrink.
  useEffect(() => {
    if (!isPhone && phoneNavOpen) dispatch(setPhoneNavOpen(false));
  }, [isPhone, phoneNavOpen, dispatch]);

  // Sync activeSection from the URL segment after SETTINGS_BASE (fall back to
  // "profile" on no match — mirrors the legacy phone selector default, §7.1).
  // The bare index path (/settings) yields no split match → "profile", as before.
  const activeSection =
    location.pathname.split(`${SETTINGS_BASE}/`)[1]?.split("/")[0] || "profile";
  useEffect(() => {
    dispatch(setActiveSection(activeSection));
  }, [dispatch, activeSection]);

  // Eyebrow: active section's GROUP label · user display name.
  const activeGroup = groups.find((g) =>
    g.items.some((i) => i.path === activeSection)
  );
  const groupLabelKey = activeGroup?.labelKey ?? "settings:nav.groupAccount";
  const displayName = userInfo
    ? `${userInfo.firstName} ${userInfo.lastName}`.trim()
    : "";
  const eyebrow = `${translate(groupLabelKey)}${displayName ? ` · ${displayName}` : ""}`;

  return (
    <RadixTooltip.Provider delayDuration={200}>
      <div
        className={`settings-v2-root p-${theme} mod-nastavenia${
          isPhone ? "" : " sv2-dotgrid"
        }`}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          // Background is provided by .settings-v2-root via the token sheet.
        }}
      >
        {isPhone ? (
          // ─── Phone <768: eyebrow + selector field + portal sheet ───
          <>
            <div style={{ padding: "12px 14px 0" }}>
              <div
                className="eyebrow"
                style={{ color: "var(--mod-accent)", marginBottom: 3 }}
              >
                {translate("common:navigation.settings")}
              </div>
              <SettingsV2SectionSelector />
            </div>
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                padding: "12px 14px 14px",
              }}
            >
              <Outlet />
            </div>
            {/* Mounted always on phone; renders null when closed. */}
            <SettingsV2PhoneNavSheet />
          </>
        ) : isTablet ? (
          // ─── Tablet 768–1279: compact head + underline tab-bar ───
          <>
            <SettingsV2PageHead
              compact
              eyebrow={eyebrow}
              title={translate("common:navigation.settings")}
            />
            <SettingsV2TabBar />
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                padding: "16px 20px 22px",
              }}
            >
              <Outlet />
            </div>
          </>
        ) : (
          // ─── Desktop ≥1280: page head + rail + scrolling column (SP0) ───
          <>
            <SettingsV2PageHead
              eyebrow={eyebrow}
              title={translate("common:navigation.settings")}
            />
            <div
              style={{
                display: "flex",
                gap: 18,
                padding: "16px 28px 24px",
                flex: 1,
                minHeight: 0,
                alignItems: "flex-start",
                overflow: "hidden",
              }}
            >
              <SettingsV2Rail />
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  maxWidth: 1020,
                  height: "100%",
                  overflowY: "auto",
                  minHeight: 0,
                }}
              >
                <Outlet />
              </div>
            </div>
          </>
        )}
      </div>
    </RadixTooltip.Provider>
  );
};

export default SettingsV2Layout;
